/**
 * The sidebar as data: one ordered list of built-in links and saved views.
 *
 * There used to be folders. Eight admin destinations behind three collapsed
 * folders meant nothing could be found by scanning, only by remembering which
 * folder someone had filed it under — and the filing was arguable (is Teaching
 * academics, or school?). So the list is flat now, and the headings you see in
 * the sidebar are worked out from the links themselves rather than arranged by
 * hand: a link is either scoped to the selected school year or it is not, which
 * is a fact about the page rather than an opinion about it. You still choose
 * the order and what to hide; you no longer choose the taxonomy.
 *
 * Everything here is pure so the customizer, the sidebar, and tests share one
 * set of rules.
 */

export type NavKey =
  | 'dashboard'
  | 'families'
  | 'enrollments'
  | 'schoolInvoices'
  | 'schedule'
  | 'attendance'
  | 'courses'
  | 'staff'
  | 'settings'
  | 'teaching'
  | 'my_family'
  | 'enrollment_status'
  | 'invoices';

export type RoleGroup = 'admin' | 'teacher' | 'family';

export interface NavItemDef {
  label: string;
  /** Label override for a role group, e.g. "My Teaching" for teachers. */
  labels?: Partial<Record<RoleGroup, string>>;
  icon: 'home' | 'users' | 'clipboard' | 'calendar' | 'attendance' | 'book' | 'graduation' | 'staff' | 'settings' | 'receipt';
  roles: RoleGroup[];
  /** The page lives under /years/:yearSlug and needs a selected year. */
  yearScoped?: boolean;
  /**
   * An administrator only gets this page when they are actually doing it. An
   * admin who teaches nothing has nothing behind a Teaching link, and offering
   * it anyway is the SIS showing them a teacher's view of an empty week.
   */
  onlyWhenTeaching?: boolean;
  path: string;
}

export const NAV_ITEMS: Record<NavKey, NavItemDef> = {
  dashboard: { label: 'Dashboard', icon: 'home', roles: ['admin', 'family'], path: 'dashboard' },
  families: { label: 'Families', icon: 'users', roles: ['admin'], path: 'families' },
  enrollments: { label: 'Enrollments', icon: 'clipboard', roles: ['admin'], yearScoped: true, path: 'enrollments' },
  // The school's invoices for the year; `invoices` below is a family's own list.
  schoolInvoices: { label: 'Invoices', icon: 'receipt', roles: ['admin'], yearScoped: true, path: 'invoices' },
  schedule: { label: 'Schedule', icon: 'calendar', roles: ['admin'], yearScoped: true, path: 'schedule' },
  attendance: { label: 'Attendance', icon: 'attendance', roles: ['admin'], yearScoped: true, path: 'attendance' },
  courses: { label: 'Courses', icon: 'book', roles: ['admin'], yearScoped: true, path: 'courses' },
  staff: { label: 'Staff', icon: 'staff', roles: ['admin'], path: 'staff' },
  settings: { label: 'School Settings', icon: 'settings', roles: ['admin'], path: 'settings' },
  teaching: {
    label: 'Teaching',
    labels: { teacher: 'My Teaching' },
    icon: 'graduation',
    roles: ['admin', 'teacher'],
    yearScoped: true,
    onlyWhenTeaching: true,
    path: 'teaching',
  },
  // Keeps its old key so a stored layout still resolves it; only what it points
  // at changed, once students got pages of their own.
  my_family: { label: 'Household', icon: 'users', roles: ['family'], path: 'household' },
  enrollment_status: { label: 'Enrollment', icon: 'clipboard', roles: ['family'], path: 'enrollment' },
  invoices: { label: 'Invoices', icon: 'receipt', roles: ['family'], path: 'invoices' },
};

export const NAV_KEYS = Object.keys(NAV_ITEMS) as NavKey[];

export interface SidebarLink { type: 'link'; key: NavKey }
export interface SidebarView { type: 'view'; id: string; name: string; url: string }
export type SidebarEntry = SidebarLink | SidebarView;

export const LAYOUT_VERSION = 3;

export interface SidebarLayout {
  version: typeof LAYOUT_VERSION;
  entries: SidebarEntry[];
}

export function roleGroupFor(role: string | null | undefined): RoleGroup | null {
  if (role === 'PRIMARY_ADMIN' || role === 'ADMIN') return 'admin';
  if (role === 'TEACHER') return 'teacher';
  if (role === 'PRIMARY_FAMILY_MEMBER') return 'family';
  return null;
}

export function navLabel(key: NavKey, roleGroup: RoleGroup | null): string {
  const def = NAV_ITEMS[key];
  return (roleGroup && def.labels?.[roleGroup]) || def.label;
}

export function navPath(key: NavKey, schoolSlug: string, yearSlug?: string | null): string | null {
  const def = NAV_ITEMS[key];
  if (def.yearScoped) {
    return yearSlug ? `/app/${schoolSlug}/years/${yearSlug}/${def.path}` : null;
  }
  return `/app/${schoolSlug}/${def.path}`;
}

export function newId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 8)}`;
}

/** Stable identity used by the customizer for drag/drop and moves. */
export function entryId(entry: SidebarEntry): string {
  return entry.type === 'link' ? `link:${entry.key}` : `view:${entry.id}`;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const link = (key: NavKey): SidebarLink => ({ type: 'link', key });

/**
 * Admin order runs Dashboard, then the year's work in the order it happens —
 * who is coming, what is offered, when it meets, who is teaching it — then the
 * records that outlive the year. School Settings is not here: it already has
 * its own place in the footer, and one destination in two rows is a choice
 * nobody wants to make.
 */
export function defaultLayout(roleGroup: RoleGroup | null): SidebarLayout {
  switch (roleGroup) {
    case 'admin':
      return {
        version: LAYOUT_VERSION,
        entries: [
          link('dashboard'),
          link('enrollments'),
          link('schoolInvoices'),
          link('courses'),
          link('schedule'),
          link('attendance'),
          link('teaching'),
          link('families'),
          link('staff'),
        ],
      };
    case 'teacher':
      return { version: LAYOUT_VERSION, entries: [link('teaching')] };
    case 'family':
      return {
        version: LAYOUT_VERSION,
        entries: [link('dashboard'), link('invoices'), link('my_family'), link('enrollment_status')],
      };
    default:
      return { version: LAYOUT_VERSION, entries: [] };
  }
}

// ---------------------------------------------------------------------------
// Validation / migration
// ---------------------------------------------------------------------------

function isNavKey(value: unknown): value is NavKey {
  return typeof value === 'string' && value in NAV_ITEMS;
}

function normalizeEntry(raw: unknown): SidebarEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  if (value.type === 'link' && isNavKey(value.key)) return { type: 'link', key: value.key };
  if (value.type === 'view' && typeof value.id === 'string' && typeof value.url === 'string') {
    return { type: 'view', id: value.id, name: String(value.name || 'Saved view'), url: value.url };
  }
  return null;
}

/**
 * Turns whatever was in storage into a layout we trust, dropping duplicates and
 * junk. A folder from a version 1 layout is not an error: its contents are
 * lifted out where the folder stood, so an old arrangement survives as the same
 * links in the same order.
 */
export function normalizeLayout(raw: unknown): SidebarLayout | null {
  if (!raw || typeof raw !== 'object') return null;
  const entries = (raw as { entries?: unknown }).entries;
  if (!Array.isArray(entries)) return null;

  const seen = new Set<string>();
  const result: SidebarEntry[] = [];
  const push = (entry: SidebarEntry | null) => {
    if (!entry) return;
    const id = entryId(entry);
    if (seen.has(id)) return;
    seen.add(id);
    result.push(entry);
  };

  for (const raw of entries) {
    const value = raw as Record<string, unknown>;
    if (value?.type === 'folder') {
      if (Array.isArray(value.items)) value.items.forEach((item) => push(normalizeEntry(item)));
      continue;
    }
    push(normalizeEntry(raw));
  }
  return { version: LAYOUT_VERSION, entries: result };
}

export interface LegacyCustomNavItem { id: string; group?: string; name: string; url: string }

/** Folds the old "custom navigation" items into a layout as saved views. */
export function migrateCustomNav(layout: SidebarLayout, legacy: LegacyCustomNavItem[]): SidebarLayout {
  if (legacy.length === 0) return layout;
  const views: SidebarView[] = legacy.map((item) => ({
    type: 'view', id: item.id, name: item.name, url: item.url,
  }));
  return withEntries([...layout.entries, ...views]);
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export interface VisibilityContext {
  roleGroup: RoleGroup | null;
  hasYear: boolean;
  /** Whether the viewer instructs any class in the selected year. */
  teachesAClass?: boolean;
}

export function linkVisible(key: NavKey, ctx: VisibilityContext): boolean {
  const def = NAV_ITEMS[key];
  if (!ctx.roleGroup || !def.roles.includes(ctx.roleGroup)) return false;
  if (def.yearScoped && !ctx.hasYear) return false;
  // Teachers keep the link whatever the answer: it is their whole sidebar, and
  // an empty teaching page tells them something a missing link would not.
  if (def.onlyWhenTeaching && ctx.roleGroup === 'admin' && !ctx.teachesAClass) return false;
  return true;
}

/** The layout with links this role cannot use (or that need a year we don't have) removed. */
export function visibleEntries(layout: SidebarLayout, ctx: VisibilityContext): SidebarEntry[] {
  return layout.entries.filter((entry) => entry.type !== 'link' || linkVisible(entry.key, ctx));
}

export function placedLinkKeys(layout: SidebarLayout): Set<NavKey> {
  const keys = new Set<NavKey>();
  for (const entry of layout.entries) if (entry.type === 'link') keys.add(entry.key);
  return keys;
}

/** Built-in pages this role could show but has taken out of the sidebar. */
export function hiddenLinks(layout: SidebarLayout, roleGroup: RoleGroup | null): NavKey[] {
  const placed = placedLinkKeys(layout);
  return NAV_KEYS.filter((key) => roleGroup && NAV_ITEMS[key].roles.includes(roleGroup) && !placed.has(key));
}

export const SAVED_GROUP_LABEL = 'Saved';

export function allViews(layout: SidebarLayout): Array<SidebarView & { group: string }> {
  return layout.entries.flatMap((entry) =>
    entry.type === 'view' ? [{ ...entry, group: SAVED_GROUP_LABEL }] : []
  );
}

// ---------------------------------------------------------------------------
// Grouping — headings are derived, never stored
// ---------------------------------------------------------------------------

export type GroupKey = 'main' | 'school' | 'saved';

export interface SidebarGroup {
  key: GroupKey;
  /** Null renders the rows with no heading above them. */
  label: string | null;
  entries: SidebarEntry[];
}

/**
 * Which group an entry belongs to, and the order the groups come in.
 *
 * Only an administrator is grouped at all, and into exactly two: the work of
 * the selected year, and the records that outlive it. That split is the one
 * this product is built on — four pages change under you when you switch years,
 * and Families and Staff do not. The first group carries no heading, because
 * which year you are in is the header's job and a second copy of that answer is
 * one more thing to read; the gap above "School" is what marks the two apart.
 * Since it is unlabelled, Dashboard sits inside it rather than above it — a gap
 * with no heading after it marks nothing. A parent or a teacher has a handful
 * of links and no such split, so grouping them would be structure for its own
 * sake.
 */
export function groupKeyFor(entry: SidebarEntry, roleGroup: RoleGroup | null): GroupKey {
  if (entry.type === 'view') return 'saved';
  if (roleGroup !== 'admin') return 'main';
  if (entry.key === 'dashboard') return 'main';
  return NAV_ITEMS[entry.key].yearScoped ? 'main' : 'school';
}

export function groupEntries(entries: SidebarEntry[], roleGroup: RoleGroup | null): SidebarGroup[] {
  const order: Array<{ key: GroupKey; label: string | null }> = roleGroup === 'admin'
    ? [
      { key: 'main', label: null },
      { key: 'school', label: 'School' },
      { key: 'saved', label: SAVED_GROUP_LABEL },
    ]
    : [
      { key: 'main', label: null },
      { key: 'saved', label: SAVED_GROUP_LABEL },
    ];

  const groups = order.map((group) => ({
    ...group,
    entries: entries.filter((entry) => groupKeyFor(entry, roleGroup) === group.key),
  }));

  const filled = groups.filter((group) => group.entries.length > 0);
  // A lone group is the whole sidebar, and a heading over the whole sidebar
  // names nothing you could have picked instead.
  return filled.length === 1 ? [{ ...filled[0], label: null }] : filled;
}

// ---------------------------------------------------------------------------
// Edits — every function returns a new layout
// ---------------------------------------------------------------------------

function withEntries(entries: SidebarEntry[]): SidebarLayout {
  return { version: LAYOUT_VERSION, entries };
}

export function findEntry(layout: SidebarLayout, id: string): { entry: SidebarEntry; index: number } | null {
  const index = layout.entries.findIndex((entry) => entryId(entry) === id);
  return index === -1 ? null : { entry: layout.entries[index], index };
}

export function removeEntry(layout: SidebarLayout, id: string): { layout: SidebarLayout; removed: SidebarEntry | null } {
  const found = findEntry(layout, id);
  if (!found) return { layout, removed: null };
  const entries = [...layout.entries];
  entries.splice(found.index, 1);
  return { layout: withEntries(entries), removed: found.entry };
}

export function insertEntry(layout: SidebarLayout, entry: SidebarEntry, index: number): SidebarLayout {
  const entries = [...layout.entries];
  entries.splice(clamp(index, entries.length), 0, entry);
  return withEntries(entries);
}

/**
 * Moves an entry to a new spot. The index is a position in the list *before*
 * the entry is removed, so callers can pass what they see on screen.
 */
export function moveEntry(layout: SidebarLayout, id: string, index: number): SidebarLayout {
  const found = findEntry(layout, id);
  if (!found) return layout;
  const target = found.index < index ? index - 1 : index;
  const { layout: without, removed } = removeEntry(layout, id);
  return removed ? insertEntry(without, removed, target) : layout;
}

/** Nudges an entry one step along the list. */
export function shiftEntry(layout: SidebarLayout, id: string, direction: -1 | 1): SidebarLayout {
  const found = findEntry(layout, id);
  if (!found) return layout;
  const next = found.index + direction;
  if (next < 0 || next >= layout.entries.length) return layout;
  return moveEntry(layout, id, next + (direction > 0 ? 1 : 0));
}

export function addView(
  layout: SidebarLayout,
  view: Omit<SidebarView, 'type' | 'id'> & { id?: string },
  index = 0
): SidebarLayout {
  const entry: SidebarView = {
    type: 'view',
    id: view.id ?? newId('view'),
    name: view.name.trim() || 'Saved view',
    url: view.url,
  };
  return insertEntry(layout, entry, index);
}

export function addLink(layout: SidebarLayout, key: NavKey, index = Number.MAX_SAFE_INTEGER): SidebarLayout {
  if (placedLinkKeys(layout).has(key)) return layout;
  return insertEntry(layout, { type: 'link', key }, index);
}

/** Trades two entries' places, which is how reordering works inside a group. */
export function swapEntries(layout: SidebarLayout, idA: string, idB: string): SidebarLayout {
  const a = findEntry(layout, idA);
  const b = findEntry(layout, idB);
  if (!a || !b) return layout;
  const entries = [...layout.entries];
  entries[a.index] = b.entry;
  entries[b.index] = a.entry;
  return withEntries(entries);
}

export function renameView(layout: SidebarLayout, viewId: string, name: string): SidebarLayout {
  return withEntries(layout.entries.map((entry) =>
    entry.type === 'view' && entry.id === viewId ? { ...entry, name } : entry
  ));
}

function clamp(index: number, length: number): number {
  return Math.max(0, Math.min(index, length));
}
